import { useTranslation } from "react-i18next";
import { IconX } from "@tabler/icons-react";
import StarsIcon from "../../../assets/icons/stars.svg?react";
import { Button } from "../../ui/Button";
import { cn } from "../../../lib/utils";
import { useTrackOnMount } from "../../../hooks/use-track-on-mount";

/**
 * The offer to turn THIS paper into a shareable poster, made in the composer
 * band.
 *
 * It lives here rather than in the transcript for a reason the transcript
 * cannot fix: the conversation pins the latest question to the top of the
 * viewport and grows a spacer so the answer streams into the room below it
 * (`GPTConversationTabs`), so anything rendered after the answer sits inside
 * that reserved slack, off screen, for the whole turn. A card in the scrollback
 * is therefore invisible exactly when the reader is most engaged. The band is
 * fixed to the bottom of the screen and is always on it.
 *
 * Same slot and same chrome as the Room proposal, deliberately: both are the
 * agent offering the reader a next step, and a second visual language for the
 * same kind of moment would read as a different product.
 *
 * Presentational on purpose: it knows nothing about eligibility, the API or the
 * router. Whether this paper may have a poster at all is settled before the
 * card is ever built.
 */
export function VisualAbstractSuggestionCard({
  articleId,
  onShown,
  onGenerate,
  onDismiss,
}: {
  /** Identifies the offer for the impression, so re-renders do not re-fire it. */
  articleId: string;
  // The impression: fired once when this paper's card reaches the screen. The
  // card reports it and does not interpret it.
  onShown: () => void;
  onGenerate: () => void;
  onDismiss: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  useTrackOnMount(
    "gpt-visual-abstract-offer-shown",
    { status: "shown", key: articleId },
    onShown
  );

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-4 rounded-2xl p-4",
        "border border-solid border-ds-slate-100",
        "bg-[rgba(253,253,253,0.9)] backdrop-blur-[3px]",
        "shadow-[0px_14px_18px_rgba(13,20,18,0.16)]"
      )}
    >
      <div className="flex w-full items-start gap-2.5">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="flex items-center gap-1.5 font-dm-sans font-semibold text-[17px]/[23px] text-ds-slate-900">
            {/* The asset bakes its own grey into every path's fill AND stroke,
                so it does not follow the text colour on its own. Recoloured
                here rather than in the shared SVG: the four other call sites
                all render it grey inside a light panel. */}
            <StarsIcon className="shrink-0 [&_path]:fill-current [&_path]:stroke-current" />
            {t("Turn this paper into a visual abstract")}
          </p>
          <p className="m-0 font-dm-sans text-footnote text-ds-slate-600">
            {t(
              "Generated from the licensed source, with evidence checks before sharing."
            )}
          </p>
        </div>
        <button
          type="button"
          aria-label={t("Close")}
          onClick={onDismiss}
          className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-[rgba(11,17,16,0.14)] text-ds-slate-800"
        >
          <IconX size={15} stroke={2.5} />
        </button>
      </div>
      <Button variant="primary" onClick={onGenerate}>
        {t("Generate visual abstract")}
      </Button>
    </div>
  );
}
